import React, { useState, useRef, useEffect, useCallback } from "react";
import { createPortal } from "react-dom";
import { FiMoreVertical } from "react-icons/fi";

/**
 * Reusable Common 3-Dots Action Menu Component (Portal-Based)
 * 
 * Renders dropdown content outside the table into `document.body` via React Portal.
 * This guarantees the menu is never clipped or obscured by:
 *  - Table overflow (`overflow-x: auto` / `overflow: hidden`)
 *  - Sticky table headers (`thead` with z-30)
 *  - Table cell/row stacking contexts
 *
 * @param {Array} actions - Array of action items:
 *   [
 *     {
 *       label: string,
 *       icon: ReactNode,
 *       onClick: function,
 *       isDanger: boolean,
 *       disabled: boolean,
 *       hidden: boolean
 *     }
 *   ]
 * @param {string} align - Dropdown alignment ('left' | 'right')
 * @param {string} direction - Dropdown direction ('auto' | 'up' | 'down')
 * @param {string} className - Additional CSS class for trigger button container
 */
export default function ActionMenu({
  actions = [],
  align = "left",
  direction = "auto",
  className = "",
}) {
  const [isOpen, setIsOpen] = useState(false);
  const [menuStyle, setMenuStyle] = useState({ top: 0, left: 0 });
  const [isRenderedUpward, setIsRenderedUpward] = useState(false);

  const buttonRef = useRef(null);
  const menuRef = useRef(null);

  const visibleActions = actions.filter((act) => !act.hidden);

  // Calculate position relative to viewport
  const updatePosition = useCallback(() => {
    if (!buttonRef.current) return;
    const rect = buttonRef.current.getBoundingClientRect();
    const menuWidth = 192; // w-48 = 192px (comfortable width for all action labels)
    const approxHeight = visibleActions.length * 40 + 16;

    let upward = false;
    if (direction === "up") {
      upward = true;
    } else if (direction === "down") {
      upward = false;
    } else {
      // Auto-detect based on viewport boundaries:
      // Only open upwards if insufficient room below AND sufficient room above
      const spaceBelow = window.innerHeight - rect.bottom;
      const spaceAbove = rect.top;
      if (spaceBelow < approxHeight + 16 && spaceAbove > approxHeight + 16) {
        upward = true;
      }
    }

    setIsRenderedUpward(upward);

    const calculatedTop = upward
      ? Math.max(8, rect.top - approxHeight - 6)
      : Math.min(window.innerHeight - approxHeight - 8, rect.bottom + 6);

    let calculatedLeft = align === "left" ? rect.left : rect.right - menuWidth;

    // Viewport edge clamping
    const minLeft = 8;
    const maxLeft = Math.max(minLeft, window.innerWidth - menuWidth - 8);
    calculatedLeft = Math.max(minLeft, Math.min(calculatedLeft, maxLeft));

    setMenuStyle({
      top: calculatedTop,
      left: calculatedLeft,
    });
  }, [align, direction, visibleActions.length]);

  // Open / Close Toggle
  const toggleMenu = (e) => {
    e.stopPropagation();
    if (!isOpen) {
      updatePosition();
      setIsOpen(true);
    } else {
      setIsOpen(false);
    }
  };

  // Close when clicking outside or pressing Escape
  useEffect(() => {
    if (!isOpen) return;

    const handleOutsideClick = (e) => {
      if (
        menuRef.current &&
        !menuRef.current.contains(e.target) &&
        buttonRef.current &&
        !buttonRef.current.contains(e.target)
      ) {
        setIsOpen(false);
      }
    };

    const handleKeyDown = (e) => {
      if (e.key === "Escape") {
        setIsOpen(false);
      }
    };

    // Auto close on scroll or window resize so menu doesn't float orphaned
    const handleScrollOrResize = () => {
      setIsOpen(false);
    };

    document.addEventListener("mousedown", handleOutsideClick, true);
    document.addEventListener("touchstart", handleOutsideClick, true);
    document.addEventListener("keydown", handleKeyDown);
    window.addEventListener("scroll", handleScrollOrResize, true);
    window.addEventListener("resize", handleScrollOrResize);

    return () => {
      document.removeEventListener("mousedown", handleOutsideClick, true);
      document.removeEventListener("touchstart", handleOutsideClick, true);
      document.removeEventListener("keydown", handleKeyDown);
      window.removeEventListener("scroll", handleScrollOrResize, true);
      window.removeEventListener("resize", handleScrollOrResize);
    };
  }, [isOpen]);

  if (visibleActions.length === 0) return null;

  return (
    <div className={`relative inline-block text-left ${className}`}>
      <button
        ref={buttonRef}
        type="button"
        onClick={toggleMenu}
        className={`w-8 h-8 rounded-lg border transition-colors flex items-center justify-center cursor-pointer focus:outline-none ${
          isOpen
            ? "bg-slate-200/90 text-slate-900 border-slate-300"
            : "bg-transparent hover:bg-slate-100 active:bg-slate-200 border-transparent hover:border-slate-200 text-slate-700 hover:text-slate-900"
        }`}
        title="More actions"
        aria-label="Actions"
        aria-haspopup="true"
        aria-expanded={isOpen}
      >
        <FiMoreVertical className="w-4 h-4" />
      </button>

      {/* Render Dropdown Menu OUTSIDE the table in document.body via Portal */}
      {isOpen &&
        createPortal(
          <div
            ref={menuRef}
            style={{
              position: "fixed",
              top: `${menuStyle.top}px`,
              left: `${menuStyle.left}px`,
              zIndex: 99999,
            }}
            className={`w-48 rounded-xl bg-white border border-slate-200 shadow-2xl shadow-slate-950/25 py-1.5 focus:outline-none transition-all duration-150 animate-in fade-in zoom-in-95 ${
              isRenderedUpward ? "origin-bottom-left" : "origin-top-left"
            }`}
            onClick={(e) => e.stopPropagation()}
          >
            {visibleActions.map((action, idx) => (
              <button
                key={action.label || idx}
                type="button"
                disabled={action.disabled}
                onClick={(e) => {
                  e.stopPropagation();
                  setIsOpen(false);
                  if (!action.disabled && action.onClick) {
                    action.onClick();
                  }
                }}
                className={`w-full flex items-center gap-2.5 px-3.5 py-2 text-xs font-semibold text-left transition-colors cursor-pointer ${
                  action.disabled
                    ? "opacity-40 cursor-not-allowed text-slate-400"
                    : action.isDanger
                    ? "text-rose-600 hover:bg-rose-50 hover:text-rose-700"
                    : "text-slate-700 hover:bg-slate-100 hover:text-slate-950"
                }`}
              >
                {action.icon && (
                  <span
                    className={`shrink-0 w-4 h-4 flex items-center justify-center ${
                      action.isDanger ? "text-rose-600" : "text-slate-500"
                    }`}
                  >
                    {action.icon}
                  </span>
                )}
                <span className="truncate">{action.label}</span>
              </button>
            ))}
          </div>,
          document.body
        )}
    </div>
  );
}
